import React, { useState, useEffect, useRef } from 'react';
import { components, withAuthenticator, Authenticator, View, Image } from '@aws-amplify/ui-react';
import { Auth, Hub, Storage } from 'aws-amplify'; 
import '@aws-amplify/ui-react/styles.css';
import { GeoJsonLayer, IconLayer } from '@deck.gl/layers';
import { Box, CircularProgress, Typography, Grid, Slider } from '@mui/material';

import './App.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Map, updateViewState, useDynamicPointColorMapping, maturityClassColors } from './components/Map';
import { SignOutButton, handleSignOut, checkUser, removeHubListener } from './components/Auth';
import ColormapLegend from './components/ColormapLegend';
import { getGeoJsonFromS3, getGZGeoJsonFromS3, getPresignedJsonFromS3, getGZSummaryTableFromS3, GetTooltipImageFromS3, listDataCollectionDates, listDataVineyards } from './components/DataRetrieval';
import { getUniqueValuesGeoJsonField , parseGeoJsonList, makeSelectorToGeoJsonNamesDict, makeMetricsByInsightCategoryDict} from './components/DataPrep';
import FloatingInputMenu from './components/KitDataMenu';
import Dashboard from './components/views/SummaryStatsViewer/SummaryStatsViewer';
import SeasonalStatsDashboard from './components/views/SeasonalStatsViewer/SeasonalStatsViewer';
import LandingDashboard from './components/views/LandingDashboard/LandingDashboard';
import { loadGeoJson } from './utils/loadGeoJson';
import { customTheme, overlayStyle } from './components/Theme';
import { Dialog, DialogContent, Drawer} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ImageRatingDrawer from './components/views/ImageViewer/ImageRatingDrawer';
import { getJsonFromS3 } from './components/views/LandingDashboard/KPICards/KPIS3';
import { transformToHierarchical, filterAndUpdateFeatures, mergeCanopyvolumeYieldFields } from './components/views/LandingDashboard/KPICards/KPIUtils';

import { API } from 'aws-amplify';
import { getClientOrgInfo } from './graphql/queries';
import { ConsoleLogger } from '@aws-amplify/core';

import './AmplifySignIn.css'

import { DataProvider, useDataSetters, useDataState } from './DataContext';
import { presignUrl } from '@aws-amplify/core/lib-esm/clients';

const login_components = {
  Header() {
    return (
      <View textAlign="center" padding="20px">
        <Image
          alt="Your Logo"
          src="/logo-full-darkgreen-small.png" // Adjust the path to your logo
        />
      </View>
    );
  },

  Footer() {
    // Define your custom footer if needed
  },
};

function App(props) {

  // App state management; see DataContext.js
  const {

    // App State
    currentUserView,
    viewState,
    userGroup,
    vineyardSummaryData,
    geoJsonVineDataFull,
    geoJsonVineData,
    geoJsonVineNoDataFull,
    geoJsonVineNoData,
    geoJsonBlockData,
    geoJsonVineMaturityDataFull,
    geoJsonVineMaturityData,
    geoJsonVineMaturityNoDataFull,
    geoJsonVineMaturityNoData,
    geoJsonInfrastructureAssetData,
    user,
    userGroups,
    selectedBucket,
    selectedVineyard,
    dataLoaded,
    selectedDate,
    selectedYear,
    selectedBlockName,
    isMaturityClassClicked,
    isInfraAssetClicked,

    // Map View State
    dialogOpen,
    dialogImageUrl,

    // Caches for file data
    fileInsightsDataCache,
    fileBlocksDataCache,

    // KPI State
    kpiSettingsTable,
  } = useDataState();

  const {
    // App State setters
    setCurrentUserView,
    setViewState,
    setUserGroup,
    setVineyardSummaryData,
    setGeoJsonVineDataFull,
    setGeoJsonVineData,
    setGeoJsonVineNoDataFull,
    setGeoJsonVineDataImgNames,
    setGeoJsonVineDataImgUrls,
    setGeoJsonVineNoData,
    setGeoJsonBlockData,
    setGeoJsonVineMaturityDataFull,
    setGeoJsonVineMaturityData,
    setGeoJsonVineMaturityNoData,
    setGeoJsonVineMaturityNoDataFull,
    setGeoJsonInfrastructureAssetData,
    setUser,
    setUserGroups,
    setSelectedBucket,
    setSelectedVineyard,
    setVineyardOptions,
    setDataLoaded,
    setDateOptions,
    setSelectedDate,
    setSelectedYear,
    setBlockNames,
    setSelectedBlockName,

    // Map View State setters
    setDialogOpen,
    setDialogImageUrl,

    // Cache State setters
    setFileInsightsDataCache,
    setFileBlocksDataCache,

    // KPI State setters
    setKpiSettingsTable,
  } = useDataSetters();

  let selectorToGeoJsonNamesDict = makeSelectorToGeoJsonNamesDict(userGroup);
  let insightMetricsByCategoryDict = makeMetricsByInsightCategoryDict(userGroup);

  const [insightMetrics, setInsightMetrics] = useState(
    Object.keys(selectorToGeoJsonNamesDict)
  );
  const [selectedInsightMetric, setSelectedInsightMetric] = useState("Trunk thickness (in.)");
  const [insightCategories, setInsightCategories] = useState(
    Object.keys(insightMetricsByCategoryDict)
  );
  const [selectedInsightCategory, setSelectedInsightCategory] = useState("Vine Health");
  const [sliderRange, setSliderRange] = useState([0.0, 35.0]);
  const [selectedSliderRange, setSelectedSliderRange] = useState([0, 100]);
  const [tooltip, setTooltip] = useState({ content: null, x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const [hoveredObject, setHoveredObject] = useState(null);
  const [boundariesExist, setBoundariesExist] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [imageRatings, setImageRatings] = useState({});

  const [activeObject, setActiveObject] = useState(null);

  // State variables from dynamodb
  // const [isMultiVineyard, setIsMultiVineyard] = useState([])

  // Initialize bucket based on user group 
  const initializeBucket = (group) => {
    if (group !== 'admin') { 
      setSelectedBucket(group) ;
    } else {
      setSelectedBucket('kiatra');
    };
  };

  // Change menu options based on user
  const updateSelectorToGeoJsonNamesDict = async (userGroup) => {
    selectorToGeoJsonNamesDict = makeSelectorToGeoJsonNamesDict(userGroup);
    return selectorToGeoJsonNamesDict
  }

  const handleBlockNameChange = async (newBlockName) => {
    setSelectedBlockName(newBlockName);
  };
  
  const handleInsightCategoryChange = async (newInsightCategory) => {
    setSelectedInsightCategory(newInsightCategory);
  };

  const handleInsightMetricChange = async (newInsightMetric) => {
    setSelectedInsightMetric(newInsightMetric);
  };

  const handleSelectedSliderRange = async (newSliderRange) => {
    setSelectedSliderRange(newSliderRange);
  };

  const handleHistogramFilterPlantPoints = (newSliderRange) => {
    setSelectedSliderRange(newSliderRange);
  };

  const getImageFileNames = async (vine_data) => {
    let imageNames = await vine_data.features.map((feature) => {
      return feature.properties.img_names_trunk_vo180 !== null
        ? feature.properties.img_names_trunk_vo180
        : feature.properties.img_names_trunk_vo0;
    });
  
    return imageNames;
  };
  

  const getImageS3URLs = async (vine_data) => {
    let imageNameS3URLs = await vine_data.features.map((feature) => ({
      img: feature.properties.imageUrl
    }));
    return imageNameS3URLs
  }

  // Need to identify the best way to create data tables via API
  // useEffect(() => {
  //   const getIsMultiVineyard = async () => {
  //     console.log('hereya', selectedBucket)
  //     const is_multi_vineyard = await API.graphql({
  //       query: getClientOrgInfo,
  //       variables: {group_id: selectedBucket}
  //     });
  //     console.log('is_multi_vineyard', is_multi_vineyard);
  //   };
  //   getIsMultiVineyard();
  // }, [selectedBucket])
  

  // useEffect(() => {
  //   console.log('isMultiVineyard', isMultiVineyard)
  // }, [isMultiVineyard, selectedBucket])

  // Hook for loading new data when bucket is changed by user
  useEffect(() => {
    const loadDataFromBucket = async () => {
      if (user) {
        setLoading(true);
  
        // Update insight metrics and categories fields in menu for specific user
        selectorToGeoJsonNamesDict = updateSelectorToGeoJsonNamesDict(userGroup);
        setInsightMetrics(Object.keys(selectorToGeoJsonNamesDict));
        insightMetricsByCategoryDict = makeMetricsByInsightCategoryDict(userGroup);
        setInsightCategories(Object.keys(insightMetricsByCategoryDict));
        
        if (selectedBucket) {
          if (selectedVineyard == null) {
            const dataVineyards = await listDataVineyards(selectedBucket);
            setVineyardOptions(dataVineyards);
            setSelectedVineyard(dataVineyards[0]);
          }
  
          if (selectedVineyard) {
            const vineyard_summary_data = await getGZSummaryTableFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/summary.csv.gz`, userGroup);
            const class_maturity_data = await getGZGeoJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/class_maturity_per_plant.json.gz`, userGroup, fileInsightsDataCache, setFileInsightsDataCache);
            const class_maturity_nodata = await getGZGeoJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/nodata_per_plant.json.gz`, userGroup, fileInsightsDataCache, setFileInsightsDataCache);
            const kpiData = await getJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/kpiSettingsTable.json`);
            const infrastructure_asset_data = await getPresignedJsonFromS3(`${selectedBucket}/${selectedVineyard}/infrastructure/points.json`, userGroup, fileInsightsDataCache, setFileInsightsDataCache);
            console.log('infrastructure_asset_data', infrastructure_asset_data) // const weakVineThreshData = await getJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/weakVineThresholds.json`);

            setVineyardSummaryData(vineyard_summary_data);
            setGeoJsonVineMaturityDataFull(class_maturity_data);
            setGeoJsonVineMaturityData(class_maturity_data);
            setGeoJsonVineMaturityNoDataFull(class_maturity_nodata);
            setGeoJsonVineMaturityNoData(class_maturity_nodata);
            setGeoJsonInfrastructureAssetData(infrastructure_asset_data);
            setKpiSettingsTable(kpiData);
            if (selectedDate == null) {
              const dataCollectionDates = await listDataCollectionDates(selectedBucket, selectedVineyard);
              setDateOptions(dataCollectionDates);
              setSelectedDate(dataCollectionDates[dataCollectionDates.length - 1]);
              setSelectedYear(dataCollectionDates[dataCollectionDates.length - 1].slice(-4));
              console.log('selected year: ', dataCollectionDates[dataCollectionDates.length - 1].slice(-4));
            }
            
            if (selectedDate) {
              const loadedBlockData = await loadBlockData(userGroup);
              const vine_data = await getGZGeoJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/${selectedDate}/insights_per_plant.json.gz`, userGroup, fileInsightsDataCache, setFileInsightsDataCache);
              const vine_nodata = await getGZGeoJsonFromS3(`${selectedBucket}/${selectedVineyard}/kit_insights/${selectedDate}/nodata_per_plant.json.gz`, userGroup, fileInsightsDataCache, setFileInsightsDataCache);
              setGeoJsonVineDataFull(vine_data);
              setGeoJsonVineData(vine_data);
              setGeoJsonVineNoDataFull(vine_nodata);
              setGeoJsonVineNoData(vine_nodata);
  
              if (vine_data && vine_data.features) {
                
                // Get image file names associated with each plant and block
                const blockNames = await getUniqueValuesGeoJsonField(loadedBlockData, 'block_name');
                blockNames.push('All Blocks');
                setBlockNames(blockNames);
                setSelectedBlockName(['All Blocks']);
                const vine_data_img_names = await getImageFileNames(vine_data);
                setGeoJsonVineDataImgNames(vine_data_img_names);
                const vine_data_img_urls = await getImageS3URLs(vine_data);
                setGeoJsonVineDataImgUrls(vine_data_img_urls);
                
              } else {
                console.log('vine_data is null or missing features');
              }
  
              // updateViewState(loadedBlockData, viewState, setViewState);
              setLoading(false);
            }
          }
        }
        console.log('selectedBucket', selectedBucket);
      }
    };
  
    loadDataFromBucket();
  }, [selectedBucket, selectedVineyard, selectedDate, selectedYear, user, isMaturityClassClicked, isInfraAssetClicked]);

  useEffect(() => {
    const calculateViewState = async () => {
      setLoading(true);
      const loadedBlockData = await loadBlockData(userGroup);
      updateViewState(loadedBlockData, viewState, setViewState);
      setLoading(false);
    }
    calculateViewState();
  }, [selectedBucket, selectedVineyard, user])
  

  useEffect(() => {
    if (user) {
      setCurrentUserView('Dashboard');
    }
  }, [user]);
  
  useEffect(() => {
    if (geoJsonVineDataFull && geoJsonVineMaturityDataFull) {
      const GeoJsonVineMaturityDataFull = mergeCanopyvolumeYieldFields(geoJsonVineDataFull, geoJsonVineMaturityDataFull);
      setGeoJsonVineMaturityDataFull(GeoJsonVineMaturityDataFull);
      console.log(GeoJsonVineMaturityDataFull);
    }
  }, [geoJsonVineMaturityDataFull, geoJsonVineDataFull]);

  useEffect(() => {
    const getBlocksForFilter = (fullDataArray) => {
      // Get all unique block names
      const allUniqueBlocks = [...new Set(fullDataArray.features.map(feature => feature.properties.block_name))];
  
      // If 'All Blocks' is selected, use allUniqueBlocks for filtering, otherwise use selectedBlockName
      const blocksForFilter = selectedBlockName == 'All Blocks' ? allUniqueBlocks : selectedBlockName;

      return blocksForFilter
    }

    const updateSelectedImageURLs = async () => {

      if (geoJsonVineDataFull) {
        
        const blocksForFilter = getBlocksForFilter(geoJsonVineDataFull)
  
        const filtered_vine_data = {
          ...geoJsonVineDataFull,
          features: geoJsonVineDataFull.features.filter(feature => {
            const value = feature.properties[selectedInsightMetricName];
            const block = feature.properties.block_name;
            return value >= selectedSliderRange[0] && value <= selectedSliderRange[1] && blocksForFilter.includes(block);
          })
        };
    
        setGeoJsonVineData(filtered_vine_data);

        // Filter images associated with vine points
        const vine_data_img_names = await getImageFileNames(filtered_vine_data);
        const vine_data_img_urls = await getImageS3URLs(filtered_vine_data);
        setGeoJsonVineDataImgNames(vine_data_img_names);
        setGeoJsonVineDataImgUrls(vine_data_img_urls);
      }

      if (geoJsonVineNoDataFull) {
        const blocksForFilter = getBlocksForFilter(geoJsonVineNoDataFull)
        const filtered_vine_nodata = {
          ...geoJsonVineNoDataFull,
          features: geoJsonVineNoDataFull.features.filter(feature => {
            const block = feature.properties.block_name;
            return blocksForFilter.includes(block);
          })
        };
    
        setGeoJsonVineNoData(filtered_vine_nodata);
      }

      if (geoJsonVineMaturityDataFull) {

        console.log('geoJsonVineDataFull', geoJsonVineDataFull);
        
        const blocksForFilter = getBlocksForFilter(geoJsonVineMaturityDataFull);

        let filtered_maturity_data;

        filtered_maturity_data = {
          ...geoJsonVineMaturityDataFull,
          features: geoJsonVineMaturityDataFull.features.filter(feature => {
            const block = feature.properties.block_name;
            return blocksForFilter.includes(block);
          })
        };

        if (kpiSettingsTable) {
          console.log('kpiSettingsTable for filtering', kpiSettingsTable[0])
          filterAndUpdateFeatures(filtered_maturity_data, kpiSettingsTable[0], 'MatureVine', 'WeakVine');
          console.log('number of weak vines: ' + filtered_maturity_data.features.filter(feature => feature.properties.class_maturity === 'WeakVine').length)
        }

        setGeoJsonVineMaturityData(filtered_maturity_data);
  
      }

      if (geoJsonVineMaturityNoDataFull) {
        
        const blocksForFilter = getBlocksForFilter(geoJsonVineMaturityNoDataFull)

        const filtered_maturity_nodata = {
          ...geoJsonVineMaturityNoDataFull,
          features: geoJsonVineMaturityNoDataFull.features.filter(feature => {
            const block = feature.properties.block_name;
            return blocksForFilter.includes(block);
          })
        };
    
        setGeoJsonVineMaturityNoData(filtered_maturity_nodata);
      }
    }
    updateSelectedImageURLs()
  }, [selectedSliderRange, selectedBlockName, selectedVineyard, selectedDate, kpiSettingsTable])
  
  

  // Helper function to load block data
  const loadBlockData = async (group) => {

    const block_data = await getGeoJsonFromS3(`${selectedBucket}/${selectedVineyard}/block_boundaries/boundaries.geojson`, group, fileBlocksDataCache, setFileBlocksDataCache);
    let boundariesExistLocal = true;
  
    if (block_data == null) {
      console.log("Error in loading boundaries.geojson");
      boundariesExistLocal = false;
      setBoundariesExist(false);
    }
    
    if (!boundariesExistLocal) {
      console.log('Waiting to load boundaries');
      // const { geoJsonData: processed_demo_block_data } = await loadGeotheme={...customTheme}Json(demo_block_data, true);
      const { geoJsonData: processed_block_data } = await loadGeoJson(block_data, true);
      setGeoJsonBlockData(processed_block_data);
      
      return block_data
      // setGeoJsonBlockData([]);
      // return demo_block_data;
    } else {
      const { geoJsonData: processed_block_data } = await loadGeoJson(block_data, true);
      setGeoJsonBlockData(processed_block_data);
      return block_data;
    }
  };
  
  // Update viewstate to be centered on user selected block
  useEffect(() => {
    if (selectedBlockName && geoJsonBlockData) {
      const block_data = geoJsonBlockData.features.find(
        (feature) => feature.properties.block_name === selectedBlockName
      );
      if (block_data) {
        updateViewState(block_data, viewState, setViewState);
      }
    }
  }, [selectedBlockName, geoJsonBlockData]);

  const onAuthStateChange = async (authState) => {
    if (authState === 'signedIn') {
      setLoading(true);

      // Upon initial sign in, get current user and FME group
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      const groups = await currentUser.signInUserSession.idToken.payload['cognito:groups'];
      const group = groups[0];
      let init_group;
      if (groups.length == 1) {
        init_group = group;
      };
      if (groups.length > 1) {
        init_group = group
      };
      setUserGroup(init_group);
      setUserGroups(groups);
      initializeBucket(init_group);
      const block_data = await loadBlockData(init_group);
      updateViewState(block_data, viewState, setViewState);

      // Extract block names from block_data geojson
      const blockNames = await getUniqueValuesGeoJsonField(block_data, 'block_name')
      blockNames.push('All Blocks')
      setBlockNames(blockNames);

      // Set selected block name state
      const selectedBlockName = await blockNames[0];
      handleBlockNameChange(selectedBlockName);
      setGeoJsonBlockData(block_data);
      updateViewState(block_data, viewState, setViewState);
      setDataLoaded(true);
    }
    else if (authState === 'signedOut') {
      setUser(null);
      setUserGroup(null);
    }
  };

// Listener for expand image within tooltip
useEffect(() => {
  const expandImageBtn = document.getElementById('expandImageBtn');
  
  if (expandImageBtn) {
    expandImageBtn.addEventListener('click', (event) => {
      setTooltip(null);
      setActiveObject(null);
      event.stopPropagation();  // prevent map onClick event
      setDialogOpen(true);
      setDialogImageUrl(hoveredObject.properties.imageUrl);
    });
  }
}, [tooltip]);  // Add tooltip as a dependency

// Handling tooltip click outside of DeckGL points
useEffect(() => {
  const handleClick = ({target}) => {
    const tooltipElement = tooltipRef.current;

    // Check if the click is outside the tooltip element
    if (tooltipElement && !tooltipElement.contains(target)) {
      setTooltip(null);
      setActiveObject(null);
    }
  };

  document.addEventListener('click', handleClick);

  // Clean up the event listener when the component unmounts
  return () => {
    document.removeEventListener('click', handleClick);
  };
}, []);


// Check authenticated user and listen for signin or signout event
useEffect(() => {
  checkUser(setUser, onAuthStateChange);
  removeHubListener(onAuthStateChange);
}, []);

// Handle sign out click
const handleSignOutClick = () => {
  handleSignOut(setUser);
};

const selectedInsightMetricName = selectedInsightMetric ? selectorToGeoJsonNamesDict[selectedInsightMetric] : null;

useEffect(() => {
  console.log('overallImageRating:', imageRatings);
}, [imageRatings]);

useEffect(() => {
  if (geoJsonVineDataFull && selectedInsightMetricName) {
    // Extract all values of the selectedInsightMetricName property from the GeoJSON data
    const values = geoJsonVineDataFull.features.map(feature => feature.properties[selectedInsightMetricName]);

    // Determine the minimum and maximum values
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Update the slider range
    setSliderRange([min, max]);
  }
}, [selectedInsightCategory, selectedInsightMetric]);

// Testing out dynamic point color mapping
const { minMetricValue, maxMetricValue, filteredFeatures, colorScale } = useDynamicPointColorMapping(
  geoJsonVineData,
  geoJsonVineDataFull,
  selectedInsightMetricName,
  selectedSliderRange,
  selectedBlockName
);

const blockLayer = [
  new GeoJsonLayer({
    id: 'geojson-block-layer',
    data: geoJsonBlockData,
    opacity: 0.1,
    stroked: true,
    filled: true,
    pickable: true,
    getLineColor: [255, 255, 255],
    getFillColor: [255, 255, 255],
  }),
]

const metricsLayers = [

  new GeoJsonLayer({
    id: 'geojson-nodata-layer',
    data: geoJsonVineNoData ? 
      geoJsonVineNoData : null,
    pickable: true,
    stroked: true,
    filled: true,
    getLineColor: f => {
      if (typeof colorScale !== "function") {
        return [0, 0, 0, 255]; // Return a default color if colorScale is not a function
      }
      if (f === hoveredObject) {
        return [255, 255, 0, 255];
      }
    },
    getFillColor: [0, 0, 0, 30],
    lineWidthScale: 20,
    lineWidthMinPixels: 1.5,
    getRadius: f => {
      if (f === hoveredObject) {
        return 1.0;
      } else {
        return 0.7;
      }
    },
    getLineWidth: 0,
    getElevation: 30,
    onClick: ({object, x, y}) => {
      
      if (!object) {
        setTooltip(null);
        setActiveObject(null);
      } else {
        const tooltipContent = object && {
          html: 
            `
            <b>No Data</b><br>
            `
          ,
          style: {
            backgroundColor: '#fff',
            fontSize: '0.8em',
            padding: '15px'
          }
        };
        const tooltipWidth = 100;
        const tooltipHeight = 20;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const paddingx = 3;
        const paddingy = 3;
  
        // Calculate horizontal position
        if (x + tooltipWidth + paddingx > windowWidth) {
          x -= tooltipWidth + 2 * paddingx;
        } else {
          x += paddingx;
        }
  
        // Calculate vertical position
        if (y + tooltipHeight + paddingy > windowHeight) {
          y -= tooltipHeight + 2 * paddingy;
        } else {
          y += paddingy - paddingy;
        }
        setTooltip({ content: tooltipContent, x, y });
      }
    },
    onHover: ({object}) => {
      setHoveredObject(object);
      if (object) {
        setActiveObject(object)
      }
    },
  }),

  new GeoJsonLayer({
    id: 'geojson-vine-layer',
    data: geoJsonVineData ? {
      ...geoJsonVineData,
      features: filteredFeatures
    } : null,
    pickable: true,
    stroked: false,
    filled: true,
    lineWidthScale: 20,
    lineWidthMinPixels: 2,
    getFillColor: f => {
      if (typeof colorScale !== "function") {
        return [0, 0, 0, 255]; // Return a default color if colorScale is not a function
      }
      const value = f.properties[selectedInsightMetricName];
      const normalizedValue = (value - minMetricValue) / (maxMetricValue - minMetricValue);
      if (f === hoveredObject) {
        return [255, 255, 0, 255];
      } else {
        return [...colorScale(normalizedValue).rgb(), colorScale(normalizedValue).alpha() * 255];
      }
    },
    getRadius: f => {
      if (f === hoveredObject) {
        return 1.0;
      } else {
        return 0.7;
      }
    },
    getLineWidth: 0,
    getElevation: 30,
    updateTriggers: {
      getFillColor: [selectedInsightMetric],
    },
    onClick: ({object, x, y}) => {
      
      if (!object) {
        setTooltip(null);
        setActiveObject(null);
      } else {
        const tooltipContent = object && {
          html: `
            <b>${selectedInsightMetric}: ${object.properties[selectedInsightMetricName].toFixed(1)}</b><br>
            <div style="position: relative;">
              <img src=${object.properties.imageUrl} width='191' height='350' display='flex'>
              <button id='expandImageBtn' style="position: absolute; top: 5px; right: 5px; background: none; border: none; border-radius:5px">
                <svg xmlns="http://www.w3.org/2000/svg" color="white" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-maximize-2">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
            </div>
          `,
          style: {
            backgroundColor: '#fff',
            fontSize: '0.8em',
            padding: '15px'
          }
        };
        const tooltipWidth = 191;
        const tooltipHeight = 350;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const paddingx = 5;
        const paddingy = 27;
  
        // Calculate horizontal position
        if (x + tooltipWidth + paddingx > windowWidth) {
          x -= tooltipWidth + 2 * paddingx;
        } else {
          x += paddingx;
        }
  
        // Calculate vertical position
        if (y + tooltipHeight + paddingy > windowHeight) {
          y -= tooltipHeight + 2 * paddingy;
        } else {
          y += paddingy - paddingy;
        }
        setTooltip({ content: tooltipContent, x, y });
      }
    },
    onHover: ({object}) => {
      setHoveredObject(object);

      if (object) {
        setActiveObject(object)
      }
    },
  })
]

const assetLayers = [
  new GeoJsonLayer({
    id: 'geojson-maturity-nodata-layer',
    data: geoJsonVineMaturityNoData ? 
    geoJsonVineMaturityNoData : null,
    pickable: true,
    stroked: true,
    filled: true,
    getLineColor: f => {
      if (typeof colorScale !== "function") {
        return [0, 0, 0, 255]; // Return a default color if colorScale is not a function
      }
      if (f === hoveredObject) {
        return [255, 255, 0, 255];
      }
    },
    getFillColor: [0, 0, 0, 30],
    lineWidthScale: 20,
    lineWidthMinPixels: 1.5,
    getRadius: f => {
      if (f === hoveredObject) {
        return 1.0;
      } else {
        return 0.7;
      }
    },
    getLineWidth: 0,
    getElevation: 30,
    onClick: ({object, x, y}) => {
      
      if (!object) {
        setTooltip(null);
        setActiveObject(null);
      } else {
        const tooltipContent = object && {
          html: 
            `
            <b>No Data</b><br>
            `
          ,
          style: {
            backgroundColor: '#fff',
            fontSize: '0.8em',
            padding: '15px'
          }
        };
        const tooltipWidth = 100;
        const tooltipHeight = 20;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const paddingx = 3;
        const paddingy = 3;
  
        // Calculate horizontal position
        if (x + tooltipWidth + paddingx > windowWidth) {
          x -= tooltipWidth + 2 * paddingx;
        } else {
          x += paddingx;
        }
  
        // Calculate vertical position
        if (y + tooltipHeight + paddingy > windowHeight) {
          y -= tooltipHeight + 2 * paddingy;
        } else {
          y += paddingy - paddingy;
        }
        setTooltip({ content: tooltipContent, x, y });
      }
    },
    onHover: ({object}) => {
      setHoveredObject(object);
      if (object) {
        setActiveObject(object)
      }
    },
  }),

  new GeoJsonLayer({
    id: 'geojson-maturity-layer',
    data: geoJsonVineMaturityData,
    pickable: false,
    stroked: false,
    filled: true,
    lineWidthScale: 20,
    lineWidthMinPixels: 2,
    getLineWidth: 0,
    getElevation: 30,
    getFillColor: f => maturityClassColors[f.properties.class_maturity] || [0, 0, 0, 255],
    getRadius: f => {
      if (f === hoveredObject) {
        return 1.0;
      } else {
        return 0.7;
      }
    },
    onClick: ({object, x, y}) => {
      if (!object) {
        setTooltip(null);
        setActiveObject(null);
      } else {
        const tooltipContent = object && {
          html: `
            <b>${selectedInsightMetric}: ${object.properties[selectedInsightMetricName].toFixed(1)}</b><br>
            <div style="position: relative;">
              <img src=${object.properties.imageUrl} width='191' height='350' display='flex'>
              <button id='expandImageBtn' style="position: absolute; top: 5px; right: 5px; background: none; border: none; border-radius:5px">
                <svg xmlns="http://www.w3.org/2000/svg" color="white" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-maximize-2">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
            </div>
          `,
          style: {
            backgroundColor: '#fff',
            fontSize: '0.8em',
            padding: '15px'
          }
        };
        const tooltipWidth = 191;
        const tooltipHeight = 350;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const paddingx = 5;
        const paddingy = 27;
  
        // Calculate horizontal position
        if (x + tooltipWidth + paddingx > windowWidth) {
          x -= tooltipWidth + 2 * paddingx;
        } else {
          x += paddingx;
        }
  
        // Calculate vertical position
        if (y + tooltipHeight + paddingy > windowHeight) {
          y -= tooltipHeight + 2 * paddingy;
        } else {
          y += paddingy - paddingy;
        }
        setTooltip({ content: tooltipContent, x, y });
      }
    },
    onHover: ({object}) => {
      setHoveredObject(object);
  
      if (object) {
        setActiveObject(object)
      }
    },
  }),

  new GeoJsonLayer({
    id: 'geojson-vine-layer',
    data: geoJsonVineData ? {
      ...geoJsonVineData,
      features: filteredFeatures
    } : null,
    pickable: true,
    stroked: true,
    filled: true,
    lineWidthScale: 20,
    lineWidthMinPixels: 2,
    getFillColor: [0, 0, 0, 0],
    getLineColor: [0, 0, 0, 0],
    getRadius: f => {
      if (f === hoveredObject) {
        return 1.0;
      } else {
        return 0.7;
      }
    },
    getLineWidth: 0,
    getElevation: 30,
    updateTriggers: {
      getFillColor: [selectedInsightMetric],
    },
    onClick: ({object, x, y}) => {
      
      if (!object) {
        setTooltip(null);
        setActiveObject(null);
      } else {
        const tooltipContent = object && {
          html: `
            <b>${selectedInsightMetric}: ${object.properties[selectedInsightMetricName].toFixed(1)}</b><br>
            <div style="position: relative;">
              <img src=${object.properties.imageUrl} width='191' height='350' display='flex'>
              <button id='expandImageBtn' style="position: absolute; top: 5px; right: 5px; background: none; border: none; border-radius:5px">
                <svg xmlns="http://www.w3.org/2000/svg" color="white" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-maximize-2">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
            </div>
          `,
          style: {
            backgroundColor: '#fff',
            fontSize: '0.8em',
            padding: '15px'
          }
        };
        const tooltipWidth = 191;
        const tooltipHeight = 350;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const paddingx = 5;
        const paddingy = 27;
  
        // Calculate horizontal position
        if (x + tooltipWidth + paddingx > windowWidth) {
          x -= tooltipWidth + 2 * paddingx;
        } else {
          x += paddingx;
        }
  
        // Calculate vertical position
        if (y + tooltipHeight + paddingy > windowHeight) {
          y -= tooltipHeight + 2 * paddingy;
        } else {
          y += paddingy - paddingy;
        }
        setTooltip({ content: tooltipContent, x, y });
      }
    },
    onHover: ({object}) => {
      setHoveredObject(object);

      if (object) {
        setActiveObject(object)
      }
    },
  })
]

// const infrastructureLayer = [
//   new GeoJsonLayer({
//     id: 'geojson-infra-layer',
//     data: geoJsonInfrastructureAssetData,
//     pointType: 'circle',
//     getPosition: d => d.geometry.coordinates,
//     stroked: true,
//     filled: true,
//     pickable: true,
//     getLineColor: [255, 255, 255],
//     getFillColor: [255, 255, 255],
//   }),
// ]

const infrastructureLayer = [new IconLayer({
  id: 'icon-infra-layer',
  data: geoJsonInfrastructureAssetData ? geoJsonInfrastructureAssetData.features.map(feature => ({
    ...feature,
    geometry: feature.geometry
  })) : [],
  getPosition: d => d.geometry.coordinates,
  getIcon: d => ({
    url: '/location-sign.png', // Ensure this path is correct
    width: 512,
    height: 512,
    anchorY: 512, // Adjust if necessary to align the icon
    mask: true,
  }),
  sizeScale: 20, // Adjust based on the desired icon size
  pickable: true,
  getColor: [255, 0, 255],
  getRadius: f => {
    if (f === hoveredObject) {
      return 1.0;
    } else {
      return 0.7;
    }
  },
  getLineWidth: 0,
  getElevation: 30,
  updateTriggers: {
    getFillColor: [selectedInsightMetric],
  },
  onClick: ({object, x, y}) => {
    
    if (!object) {
      setTooltip(null);
      setActiveObject(null);
    } else {
      const tooltipContent = object && {
        html: `
          <div style="position: relative;">
            <b>Item: </b>${object.properties.label}<br>
            <img src=${object.properties.imageUrl} width='191' height='350' display='flex'>
            <button id='expandImageBtn' style="position: absolute; top: 5px; right: 5px; background: none; border: none; border-radius:5px">
              <svg xmlns="http://www.w3.org/2000/svg" color="white" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="feather feather-maximize-2">
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
          </div>
        `,
        style: {
          backgroundColor: '#fff',
          fontSize: '0.8em',
          padding: '15px'
        }
      };
      const tooltipWidth = 191;
      const tooltipHeight = 350;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const paddingx = 5;
      const paddingy = 27;

      // Calculate horizontal position
      if (x + tooltipWidth + paddingx > windowWidth) {
        x -= tooltipWidth + 2 * paddingx;
      } else {
        x += paddingx;
      }

      // Calculate vertical position
      if (y + tooltipHeight + paddingy > windowHeight) {
        y -= tooltipHeight + 2 * paddingy;
      } else {
        y += paddingy - paddingy;
      }
      setTooltip({ content: tooltipContent, x, y });
    }
  },
  onHover: ({object}) => {
    setHoveredObject(object);

    if (object) {
      setActiveObject(object)
    }
  },
})];


const layers = [
  blockLayer,
  ...(isMaturityClassClicked ? assetLayers : metricsLayers),
  ...(isInfraAssetClicked ? infrastructureLayer : []),
]

// Upon authorization, show user email and signout button
// if (!geoJsonVineDataFull || !geoJsonBlockData) {
//     return (
//       <div key={user ? 'loggedIn' : 'loggedOut'} style={{ position: 'relative', height: '100vh' }}>
//         <SignOutButton user={user} handleSignOut={handleSignOut} />
//       </div>
//     );
// };

const handleRatingChange = (newRating, ratingType) => {
  setImageRatings(prevRatings => {
    const imageUrl = dialogImageUrl.split('?')[0];
    const user_name = user.attributes.email;
    console.log('user_name', user_name)
    const updatedRatings = {
      ...prevRatings,
      [imageUrl]: {
        ...(prevRatings[imageUrl] || {}),
        ['user_id']: user_name,
        [ratingType]: newRating
      }
    };

    return updatedRatings;
  });
};

// Render App
return (
  <Authenticator hideSignUp={true} components={login_components}>
    {({ signOut, user }) => (
      <div key={user ? 'loggedIn' : 'loggedOut'} style={{ position: 'relative', height: '100vh' }}>
        {tooltip && tooltip.content && (
          <div
            ref={tooltipRef}
            style={{
              position: "absolute",
              zIndex: 1000,
              pointerEvents: "auto",
              left: tooltip.x,
              top: tooltip.y,
              ...tooltip.content.style
            }}
            dangerouslySetInnerHTML={{ __html: tooltip.content.html }}
          />
        )}

        {currentUserView === 'Dashboard' ? (
          <>
            <DialogContent>
              <LandingDashboard />
            </DialogContent> 

            <SignOutButton 
                  user={user} 
                  handleSignOut={handleSignOutClick} 
                />
          </> ) : (
            <>
              <Map
                key={dataLoaded}
                viewState={viewState}
                onViewStateChange={({ viewState }) => setViewState(viewState)}
                controller={true}
                layers={layers}
              />
              {geoJsonVineData ? <FloatingInputMenu 
                showBucketDropdown={userGroups.length > 1}
                insightCategories={insightCategories}
                selectedInsightCategory={selectedInsightCategory}
                onInsightCategoryChange={handleInsightCategoryChange}
                insightMetrics={insightMetrics}
                selectedInsightMetric={selectedInsightMetric}
                onInsightMetricChange={handleInsightMetricChange}
                sliderRange={sliderRange}
                selectedSliderRange={selectedSliderRange}
                onSelectedSliderRange={handleSelectedSliderRange}
                boundariesExist={boundariesExist}
                fileBlocksDataCache={fileBlocksDataCache}
                setFileBlocksDataCache={setFileBlocksDataCache}
                setVineyardSummaryData={setVineyardSummaryData}
                vineyardSummaryData={vineyardSummaryData}
              /> : 'Loading...'}

              <DialogContent>
                {geoJsonVineData ? <Dashboard 
                  features={geoJsonVineData.features}
                  userGroup={userGroup} 
                /> : 'Loading...'}
              </DialogContent>

              <DialogContent>
                {vineyardSummaryData ? <SeasonalStatsDashboard 
                /> : 'Loading...'}
              </DialogContent>

              <ColormapLegend
                minMetricValue={minMetricValue}
                maxMetricValue={maxMetricValue}
                selectedInsightMetric={selectorToGeoJsonNamesDict[selectedInsightMetric]}
                selectedInsightMetricName={selectedInsightMetricName}
                userGroup={userGroup}
                geojsonData={geoJsonVineData}
                geoJsonVineMaturityData={geoJsonVineMaturityData}
                geoJsonVineMaturityNoData={geoJsonVineMaturityNoData}
                onHistogramFilterPlantPoints={handleHistogramFilterPlantPoints}
              />

              <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogContent>
                  <div style={{ position: 'relative', height: `${0.85 * window.innerHeight}px`, overflow: 'clip' }}>
                    <TransformWrapper
                      initialScale={0.85 * window.innerHeight / 1920}
                      initialPositionX={0.2 * 1080 * 0.85 * window.innerHeight / 1920}
                      doubleClick={{mode: 'reset'}}
                      minScale={0.85 * window.innerHeight / 1920}
                      limitToBounds={false}
                    >
                      {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                        <React.Fragment>
                          <TransformComponent>
                            <img src={dialogImageUrl} />
                          </TransformComponent>
                        </React.Fragment>
                      )}
                    </TransformWrapper>
                  </div>

                  <ImageRatingDrawer
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                    imageRatings={imageRatings}
                    handleRatingChange={handleRatingChange}
                    dialogImageUrl={dialogImageUrl}
                  />

                </DialogContent>
              </Dialog>

              <SignOutButton 
                user={user} 
                handleSignOut={handleSignOutClick} 
              />
            </>
          )}


      </div>
    )}
  </Authenticator>
);
}

export default App;

// export default withAuthenticator(
// App, { 
//   theme: {...customTheme},
//   hideSignUp: true
// });